import {
  ActionConfirmationModal,
  Button,
  Checkbox,
  Typography,
} from "@/components/ui";
import { NotificationItem } from "@/components/ui/DashboardNotificationsCard/NotificationItem";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import {
  notificationMatchesCategory,
  type NotificationCategory,
} from "@/components/ui/NotificationItems/notificationCategories";
import {
  getInfiniteNotificationInboxItemsOptions,
  getNotificationInboxItemsOptions,
  useDeleteNotificationInboxItem,
  useUpdateNotificationInboxItemsReadState,
  useUpdateNotificationInboxReadState,
} from "@/lib/data/react-query";
import { type NotificationInboxItem } from "@/lib/data/requests";
import { searchAllMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import { loadLatestNotificationInbox } from "@/lib/data/react-query/matters/notification-inbox-refresh";
import { cn } from "@/lib/utils/cn";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import toast from "react-hot-toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  ListChecks,
  MessageSquareText,
  Mail,
  MailOpen,
  StickyNote,
  X,
} from "lucide-react";

interface NotificationItemsProps {
  category?: NotificationCategory;
  onContextMenuOpenChange?: (open: boolean) => void;
  onNotificationSelect?: (notification: NotificationInboxItem) => void;
  onCountChange?: (summary: { count: number; hasMore: boolean }) => void;
}

export const NotificationItems = ({
  category = "all",
  onContextMenuOpenChange,
  onNotificationSelect,
  onCountChange,
}: NotificationItemsProps) => {
  const [activeFilters, setActiveFilters] = useState<Set<string>>(
    () => new Set(["unread"]),
  );
  const apiFilters = useMemo(
    () => ({
      unread: activeFilters.has("unread") || undefined,
      tasks: activeFilters.has("task") || undefined,
      texts: activeFilters.has("text") || undefined,
      notes: activeFilters.has("note") || undefined,
    }),
    [activeFilters],
  );
  const {
    data,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(getInfiniteNotificationInboxItemsOptions(apiFilters));
  const queryClient = useQueryClient();
  const { data: latestHead, isPlaceholderData: isHeadPlaceholder } = useQuery(
    getNotificationInboxItemsOptions({ filters: apiFilters }),
  );
  const listTopRef = useRef<HTMLDivElement>(null);
  const [isLoadingLatest, setIsLoadingLatest] = useState(false);
  const updateReadState = useUpdateNotificationInboxReadState();
  const updateNotificationsReadState =
    useUpdateNotificationInboxItemsReadState();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const selectionAnchorIdRef = useRef<string | null>(null);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState<
    Set<string>
  >(() => new Set());
  const [isBulkMarking, setIsBulkMarking] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const deleteNotification = useDeleteNotificationInboxItem();
  const uniqueNotifications = useMemo(
    () => data?.pages.flatMap((page) => page.data.notifications) ?? [],
    [data?.pages],
  );
  const handleLoadLatest = async () => {
    if (isLoadingLatest) return;
    setIsLoadingLatest(true);
    try {
      await loadLatestNotificationInbox(queryClient, apiFilters);
      setSelectedNotificationIds(new Set());
      selectionAnchorIdRef.current = null;
      listTopRef.current?.scrollIntoView({ block: "start" });
    } catch {
      toast.error("Couldn't load new notifications. Please try again.");
    } finally {
      setIsLoadingLatest(false);
    }
  };
  const notificationMatterIds = useMemo(
    () => [
      ...new Set(
        uniqueNotifications
          .map((notification) => notification.destination?.matterId)
          .filter((id): id is string => Boolean(id)),
      ),
    ],
    [uniqueNotifications],
  );
  const { data: mattersData } = useQuery({
    ...searchAllMattersQueryOptions({
      matterIds: notificationMatterIds,
      pageSize: 500,
    }),
    enabled: notificationMatterIds.length > 0,
  });
  const hasMore = Boolean(hasNextPage);
  const isSinglePending = updateReadState.isPending;
  const filters = [
    {
      id: "unread",
      label: "Unread",
      Icon: Bell,
    },
    ...(category === "communication"
      ? [
          {
            id: "sms",
            label: "SMS",
            Icon: MessageSquareText,
          },
          {
            id: "email",
            label: "Email",
            Icon: Mail,
          },
        ]
      : [
          {
            id: "task",
            label: "Tasks",
            Icon: ListChecks,
          },
          ...(category === "all"
            ? [
                {
                  id: "text",
                  label: "Texts",
                  Icon: MessageSquareText,
                },
              ]
            : []),
          {
            id: "note",
            label: "Notes",
            Icon: StickyNote,
          },
        ]),
  ];
  const matchesActiveCategory = useCallback(
    (notification: NotificationInboxItem) => {
      if (!notificationMatchesCategory(notification, category)) return false;
      if (category !== "communication") return true;
      const types = ["sms", "email"].filter((type) => activeFilters.has(type));
      return (
        !types.length ||
        types.some((type) =>
          notification.eventType.includes(type.toUpperCase()),
        )
      );
    },
    [activeFilters, category],
  );
  const filteredNotifications = useMemo(
    () => uniqueNotifications.filter(matchesActiveCategory),
    [uniqueNotifications, matchesActiveCategory],
  );
  const newNotificationsAvailable = Boolean(
    data &&
      data.pages.length > 1 &&
      !isHeadPlaceholder &&
      latestHead?.data.notifications.some(
        (notification) =>
          matchesActiveCategory(notification) &&
          !uniqueNotifications.some((loaded) => loaded.id === notification.id),
      ),
  );
  const matterTitleById = useMemo(
    () =>
      new Map(
        (mattersData?.data ?? []).map((matter) => [matter.id, matter.title]),
      ),
    [mattersData?.data],
  );
  const toggleFilter = (filter: string) => {
    setActiveFilters((previous) => {
      const next = new Set(previous);
      if (next.has(filter)) next.delete(filter);
      else next.add(filter);
      return next;
    });
  };
  const toggleSelection = useCallback(
    (notificationId: string, shiftKey: boolean) => {
      const anchorIndex = filteredNotifications.findIndex(
        (notification) => notification.id === selectionAnchorIdRef.current,
      );
      const notificationIndex = filteredNotifications.findIndex(
        (notification) => notification.id === notificationId,
      );
      const hasRange =
        shiftKey && anchorIndex !== -1 && notificationIndex !== -1;

      if (!hasRange) selectionAnchorIdRef.current = notificationId;

      setSelectedNotificationIds((previous) => {
        const next = new Set(previous);

        if (hasRange) {
          const shouldSelect = !previous.has(notificationId);
          const rangeStart = Math.min(anchorIndex, notificationIndex);
          const rangeEnd = Math.max(anchorIndex, notificationIndex);

          filteredNotifications
            .slice(rangeStart, rangeEnd + 1)
            .forEach((notification) => {
              if (shouldSelect) next.add(notification.id);
              else next.delete(notification.id);
            });

          return next;
        }

        if (next.has(notificationId)) next.delete(notificationId);
        else next.add(notificationId);
        return next;
      });
    },
    [filteredNotifications],
  );
  const selectForContextMenu = useCallback((notificationId: string) => {
    setSelectedNotificationIds((previous) =>
      previous.has(notificationId) ? previous : new Set([notificationId]),
    );
  }, []);

  useEffect(() => {
    setSelectedNotificationIds(new Set());
    selectionAnchorIdRef.current = null;
  }, [apiFilters, category]);

  useEffect(() => {
    onCountChange?.({
      count: filteredNotifications.filter(
        (notification) => !notification.readAt,
      ).length,
      hasMore,
    });
  }, [filteredNotifications, hasMore, onCountChange]);

  const handleMarkRead = (notification: NotificationInboxItem) => {
    if (notification.readAt || isSinglePending || isBulkMarking) return;
    updateReadState.mutate({ id: notification.id, read: true });
  };

  const updateSelectedReadState = useCallback(
    async (read: boolean) => {
      const ids = uniqueNotifications
        .filter(
          (notification) =>
            selectedNotificationIds.has(notification.id) &&
            Boolean(notification.readAt) !== read,
        )
        .map((notification) => notification.id);
      if (!ids.length || isSinglePending || isBulkMarking) return;
      setIsBulkMarking(true);
      try {
        const batches = Array.from(
          { length: Math.ceil(ids.length / 100) },
          (_, index) => ids.slice(index * 100, (index + 1) * 100),
        );
        for (const notificationIds of batches) {
          await updateNotificationsReadState.mutateAsync({
            notificationIds,
            read,
          });
        }
        setSelectedNotificationIds(new Set());
        selectionAnchorIdRef.current = null;
        toast.success(
          `${ids.length} notifications marked as ${read ? "read" : "unread"}`,
        );
      } finally {
        setIsBulkMarking(false);
      }
    },
    [
      isBulkMarking,
      isSinglePending,
      updateNotificationsReadState,
      selectedNotificationIds,
      uniqueNotifications,
    ],
  );
  const selectedNotifications = useMemo(
    () =>
      uniqueNotifications.filter((notification) =>
        selectedNotificationIds.has(notification.id),
      ),
    [selectedNotificationIds, uniqueNotifications],
  );
  const canMarkSelectedRead = selectedNotifications.some(
    (notification) => !notification.readAt,
  );
  const canMarkSelectedUnread = selectedNotifications.some((notification) =>
    Boolean(notification.readAt),
  );

  const allVisibleSelected =
    filteredNotifications.length > 0 &&
    filteredNotifications.every((notification) =>
      selectedNotificationIds.has(notification.id),
    );
  const toggleSelectAll = () => {
    if (allVisibleSelected) selectionAnchorIdRef.current = null;

    setSelectedNotificationIds((previous) => {
      if (allVisibleSelected) {
        return new Set(
          Array.from(previous).filter(
            (id) =>
              !filteredNotifications.some(
                (notification) => notification.id === id,
              ),
          ),
        );
      }

      return new Set([
        ...previous,
        ...filteredNotifications.map((notification) => notification.id),
      ]);
    });
  };

  const handleRetrieveMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || isLoadingLatest) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoadingLatest]);

  useEffect(() => {
    const loadMoreTrigger = loadMoreRef.current;
    if (!hasNextPage || !loadMoreTrigger) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleRetrieveMore();
        }
      },
      { rootMargin: "200px 0px" },
    );

    observer.observe(loadMoreTrigger);
    return () => {
      observer.disconnect();
    };
  }, [handleRetrieveMore, hasNextPage]);

  if (error) {
    return (
      <Typography variant="subHeading" className="text-red-500">
        Error loading notifications
      </Typography>
    );
  }

  return (
    <div ref={listTopRef} className="space-y-0">
      <div className="bg-popover sticky top-0 z-10 h-[65px] overflow-hidden border-b">
        <AnimatePresence initial={false} mode="popLayout">
          {selectedNotificationIds.size ? (
            <motion.div
              key="selection-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.18 }}
              className="bg-muted/50 absolute inset-0 flex items-center gap-2 px-4"
            >
              <Checkbox
                checked={allVisibleSelected}
                aria-label="Select all notifications"
                onCheckedChange={toggleSelectAll}
              />
              <Button
                type="button"
                variant="ghost"
                className="h-8 px-2"
                onClick={() => {
                  void updateSelectedReadState(true);
                }}
                disabled={isBulkMarking}
              >
                <MailOpen className="mr-1.5 h-4 w-4" />
                Mark as read
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 px-2"
                onClick={() => {
                  void updateSelectedReadState(false);
                }}
                disabled={isBulkMarking}
              >
                <Mail className="mr-1.5 h-4 w-4" />
                Mark as unread
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-auto h-8 w-8"
                aria-label="Clear notification selection"
                onClick={() => {
                  setSelectedNotificationIds(new Set());
                  selectionAnchorIdRef.current = null;
                }}
              >
                <X className="h-5 w-5" />
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="filters"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 flex gap-2 overflow-x-auto px-4 py-4"
            >
              {filters.map(({ id, label, Icon }) => (
                <Button
                  key={id}
                  type="button"
                  size="sm"
                  variant="outline"
                  className={cn(
                    "shrink-0 rounded-full",
                    activeFilters.has(id) &&
                      "border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 dark:hover:text-slate-900",
                    id === "unread" &&
                      activeFilters.has(id) &&
                      "midnight:border-primary midnight:bg-primary midnight:text-primary-foreground midnight:hover:bg-primary/90 midnight:hover:text-primary-foreground border-blue-500 bg-blue-600 text-white hover:bg-blue-700 hover:text-white dark:border-blue-400 dark:bg-blue-500 dark:hover:bg-blue-400",
                  )}
                  onClick={() => {
                    toggleFilter(id);
                  }}
                >
                  <Icon className="mr-1.5 h-4 w-4" />
                  {label}
                </Button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {newNotificationsAvailable ? (
        <div className="bg-popover sticky top-[65px] z-10 border-b px-4 py-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={isLoadingLatest}
            onClick={() => void handleLoadLatest()}
          >
            {isLoadingLatest
              ? "Loading new notifications..."
              : "New notifications available"}
          </Button>
        </div>
      ) : null}
      <div className="relative">
        {filteredNotifications.length === 0 ? (
          <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 px-8 text-center">
            <Typography variant="subHeading">
              {isFetching
                ? "Loading notifications..."
                : activeFilters.has("unread")
                  ? "You're all caught up!"
                  : "No notifications match these filters."}
            </Typography>
            {!isFetching && activeFilters.has("unread") ? (
              <Button
                type="button"
                variant="link"
                onClick={() => {
                  toggleFilter("unread");
                }}
              >
                See read notifications
              </Button>
            ) : null}
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onSelect={() => onNotificationSelect?.(notification)}
              onNotificationClick={() => {
                handleMarkRead(notification);
              }}
              onContextMenu={() => {
                selectForContextMenu(notification.id);
              }}
              onContextMenuOpenChange={onContextMenuOpenChange}
              onUpdateSelectedReadState={(read) => {
                void updateSelectedReadState(read);
              }}
              canMarkRead={canMarkSelectedRead}
              canMarkUnread={canMarkSelectedUnread}
              isUpdatingSelectedReadState={isBulkMarking}
              selectedNotificationCount={selectedNotifications.length}
              isSelected={selectedNotificationIds.has(notification.id)}
              onToggleSelect={toggleSelection}
              onDelete={() => {
                setPendingDeleteId(notification.id);
              }}
              matterTitle={
                notification.destination
                  ? (matterTitleById.get(notification.destination.matterId) ??
                    undefined)
                  : undefined
              }
            />
          ))
        )}
        {isFetching &&
        !isFetchingNextPage &&
        filteredNotifications.length > 0 ? (
          <div className="bg-background/55 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px]">
            <LoadingSpinner className="h-5 w-5" />
          </div>
        ) : null}
      </div>
      {hasNextPage && (
        <div ref={loadMoreRef} className="flex h-12 justify-center pt-2">
          {isFetchingNextPage && <LoadingSpinner className="h-4 w-4" />}
        </div>
      )}
      <ActionConfirmationModal
        open={Boolean(pendingDeleteId)}
        setOpen={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
        title="Delete notification?"
        description="This action cannot be undone."
        buttonText="Delete"
        isActionPending={deleteNotification.isPending}
        afterActionFn={async () => {
          if (!pendingDeleteId) return;
          try {
            await deleteNotification.mutateAsync(pendingDeleteId);
            setSelectedNotificationIds((selected) => {
              const next = new Set(selected);
              next.delete(pendingDeleteId);
              return next;
            });
            setPendingDeleteId(null);
          } catch {
            // The mutation shows the API error and leaves the dialog open.
          }
        }}
      />
    </div>
  );
};
